import React, { useState, useEffect, useMemo } from 'react';
import { FaFileWord, FaCog } from 'react-icons/fa';
import Card from '../../../components/Card/Card';
import Button from '../../../components/buttons/Button';
import Input from '../../../components/inputs/Input';
import Toggle from '../../../components/Toggle/Toggle';
import Picker from '../../../components/inputs/Picker';
import { reportTypeService } from '../../../services';
import { useAuth } from '../../../context/AuthContext';
import './steps.css';

function StepPreview({ sections, templateConfig, onConfigChange, logoUrl }) {
  const [showConfig, setShowConfig] = useState(false);
  const [favoriteReportTypes, setFavoriteReportTypes] = useState([]);
  const { profile } = useAuth();
  
  // Load favorite report types
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const favorites = await reportTypeService.getFavorites();
        if (favorites && favorites.length > 0) {
          setFavoriteReportTypes(favorites);
        } else {
          // Use profile favorites as fallback
          const profileFavs = profile?.reportTypeFavorites?.getFavorites?.() || [];
          setFavoriteReportTypes(profileFavs);
        }
      } catch (error) {
        // Use profile favorites as fallback
        const profileFavs = profile?.reportTypeFavorites?.getFavorites?.() || [];
        setFavoriteReportTypes(profileFavs);
      }
    };
    loadFavorites();
  }, [profile]);

  // Update config field
  const updateConfig = (field, value) => {
    onConfigChange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Render formatted section preview
  const renderSectionPreview = (section) => {
    if (!section.isVisible) return null;

    const titleSlug = toSlug(section.title);

    const alignment = section.alignment || (section.format === 'table' ? 'center' : 'left');
    const alignStyle = { textAlign: alignment };
    
    switch (section.format) {
      case 'inline':
        return (
          <div className="preview-section-inline" style={alignStyle}>
            <strong>{section.title}:</strong> {section.content || ''}
          </div>
        );

      case 'table':
        return (
          <div className="preview-section-table" style={alignStyle}>
            {section.content ? (
              <table className="preview-table" style={{ margin: alignment === 'center' ? '0 auto' : alignment === 'right' ? '0 0 0 auto' : '0' }}>
                <tbody>
                  {section.content.split('\n').filter(l => l.trim()).map((line, idx) => (
                    <tr key={idx}>
                      {line.split('---').map((cell, cellIdx) => (
                        <td key={cellIdx}>{cell.trim()}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="preview-placeholder"></div>
            )}
          </div>
        );

      case 'list':
        return (
          <div className="preview-section-list" style={alignStyle}>
            <h3>{section.title}</h3>
            {section.subtitle && <p className="preview-subtitle">{section.subtitle}</p>}
            {section.content ? (
              <ul className="preview-list">
                {section.content.split('\n').filter(l => l.trim()).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>
        );

      case 'paragraph':
      default:
        return (
          <div className="preview-section-paragraph" style={alignStyle}>
            <h3>{section.title}</h3>
            {section.subtitle && <p className="preview-subtitle">{section.subtitle}</p>}
            {section.content && (
              <div className="preview-content">
                {section.content}
              </div>
            )}
          </div>
        );
    }
  };

  // Utility functions
  const toSlug = (text) => {
    return (text || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, '')
      .replace(/^(.)/, m => m.toLowerCase());
  };

  const toPascalCase = (text) => {
    return (text || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map(s => s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : '')
      .join('');
  };

  return (
    <div className="step-preview">
      {/* Header */}
      <div className="step-preview-header">
        <div className="step-preview-actions">
          <Button
            variant="secondary"
            size="small"
            icon={FaCog}
            onClick={() => setShowConfig(!showConfig)}
          >
            {showConfig ? 'Masquer' : 'Configuration'}
          </Button>
        </div>
      </div>

      {/* Settings Panel - Toggleable */}
      {showConfig && (
      <Card className="step-preview-settings">
          <h4>Configuration</h4>
          <div className="step-settings-grid">
            <div className="step-setting-item">
              <Input
                label="Nom du template"
                value={templateConfig.name}
                onChange={(e) => updateConfig('name', e.target.value)}
                placeholder="Ex: Rapport médical standard"
              />
            </div>
            <div className="step-setting-item">
              <Picker
                label="Type de rapport associé"
                value={templateConfig.reportType}
                onChange={(e) => updateConfig('reportType', e.target.value)}
              >
                <option value="">Sélectionner un type</option>
                {favoriteReportTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Picker>
            </div>
            <div className="step-setting-item">
              <label className="step-setting-label">
                <Toggle
                  checked={templateConfig.includeLogo}
                  onChange={(checked) => updateConfig('includeLogo', checked)}
                />
                <span>Inclure le logo</span>
              </label>
            </div>
          </div>
      </Card>
      )}

      {/* Document Preview - Full Width */}
      <div className="step-preview-document">
        {/* Page */}
        <div className="preview-page">
            {/* Logo */}
            {templateConfig.includeLogo && (
              <div className="preview-logo">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" />
                ) : (
                  <div className="preview-logo-placeholder">
                    <FaFileWord /> Logo
                  </div>
                )}
              </div>
            )}

            {/* Sections */}
            <div className="preview-sections">
              {sections.filter(s => s.isVisible).map((section, index) => (
                <div key={section.id} className="preview-section">
                  {renderSectionPreview(section)}
                </div>
              ))}
            </div>

        </div>
      </div>
    </div>
  );

  function generateTemplateSyntax(sections, config) {
    let syntax = '';
    
    if (config.includeLogo) {
      syntax += '{#logoSection}\n{%logo}\n{/logoSection}\n\n';
    }

    sections.filter(s => s.isVisible).forEach(section => {
      const titleSlug = toSlug(section.title);
      
      switch (section.format) {
        case 'inline':
          syntax += `${section.title}: {${titleSlug}}\n\n`;
          break;
        case 'table':
          syntax += `{@tableau${toPascalCase(section.title)}}\n`;
          if (section.content) {
            section.content.split('\n').filter(l => l.trim()).forEach(line => {
              syntax += `${line}\n`;
            });
          }
          syntax += '\n';
          break;
        case 'list':
          syntax += `${section.title}\n`;
          if (section.subtitle) {
            syntax += `${section.subtitle}\n`;
          }
          syntax += `{#${titleSlug}}\n - {}\n{/${titleSlug}}\n\n`;
          break;
        case 'paragraph':
        default:
          syntax += `{#${titleSlug}Section}\n`;
          syntax += `${section.title}\n`;
          if (section.subtitle) {
            syntax += `${section.subtitle}\n`;
          }
          syntax += `{#contenu}{.}{/contenu}\n`;
          syntax += `{/${titleSlug}Section}\n\n`;
          break;
      }
    });

    return syntax;
  }
}

export default StepPreview;